import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../auth/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Por default: ayer (el reporte ejecutivo se ve sobre el día cerrado)
function ayerIso() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

const CUMPL_STYLE = {
  OK:                 { bg: "#DCFCE7", color: "#166534", label: "OK", icon: "fa-circle-check" },
  ALERTA:             { bg: "#FEF3C7", color: "#92400E", label: "Alerta", icon: "fa-triangle-exclamation" },
  INCUMPLIDO:         { bg: "#FEE2E2", color: "#991B1B", label: "Incumplido", icon: "fa-circle-xmark" },
  EXENTO_SUSPENSION:  { bg: "#CFFAFE", color: "#155E75", label: "Exento (suspensión)", icon: "fa-cloud-bolt" },
  SIN_PLAN:           { bg: "#F1F5F9", color: "#475569", label: "Sin plan", icon: "fa-minus" },
};

export default function ReporteDiarioModule() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [fecha, setFecha] = useState(ayerIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const cargar = useCallback(async (f) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/supervisor/reporte-diario`, { params: { fecha: f } });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar el reporte");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(fecha); }, [fecha, cargar]);

  const descargarPDF = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/supervisor/reporte-diario/pdf`, {
        params: { fecha }, responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `DMU614_Reporte_Diario_${fecha}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al generar PDF");
    } finally { setDownloading(false); }
  };

  return (
    <div className="hub-bg" data-testid="page-reporte-diario">
      <header className="topnav">
        <div className="brand">
          {user?.rol !== "director" && (
            <button onClick={() => navigate("/hub")} className="iconbtn mr-1" aria-label="Atrás" data-testid="btn-back-hub-reporte">
              <i className="fa-solid fa-arrow-left"/>
            </button>
          )}
          <div className="logo" style={{background: "#0EA5E9"}}><i className="fa-solid fa-file-lines"/></div>
          <div>
            <div className="title">Reporte Diario</div>
            <div className="sub">DMU 614 · Documento ejecutivo para Dirección</div>
          </div>
        </div>
        <div className="actions">
          <div className="hidden sm:block text-right mr-2">
            <div className="text-sm font-semibold leading-tight">{user?.nombre}</div>
            <div className="text-xs text-slate-500">{user?.rol}</div>
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="btn-logout-reporte" style={{padding:"0.5rem 0.9rem"}}>
            <i className="fa-solid fa-right-from-bracket mr-1"/>Salir
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Toolbar */}
        <section className="card-base mb-5" data-testid="reporte-toolbar">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600 block mb-1">Fecha del reporte</label>
              <input
                type="date" className="cap-input"
                value={fecha} max={new Date().toISOString().slice(0,10)}
                onChange={e => setFecha(e.target.value)}
                data-testid="reporte-fecha-input"
              />
            </div>
            <button
              className="btn-ghost" onClick={() => setFecha(ayerIso())}
              data-testid="btn-ayer" style={{padding:"0.55rem 0.9rem"}}
            >
              <i className="fa-solid fa-rotate-left mr-1"/>Ayer
            </button>
            <button
              className="btn-primary" disabled={!data || downloading} onClick={descargarPDF}
              data-testid="btn-descargar-pdf"
            >
              {downloading ? "Generando…" : (<><i className="fa-solid fa-file-pdf mr-1"/>Descargar PDF</>)}
            </button>
            <div className="flex-1"/>
            <div className="text-xs text-slate-500">
              <i className="fa-solid fa-circle-info mr-1"/>
              Envío automático WhatsApp/email al Director: <strong>pendiente API keys</strong> (Fase 2).
            </div>
          </div>
        </section>

        {loading && (
          <div className="card-base text-center py-12 text-slate-400" data-testid="reporte-loading">
            <i className="fa-solid fa-circle-notch fa-spin text-3xl mb-3"/>
            <div>Cargando reporte…</div>
          </div>
        )}

        {!loading && data && <ReporteRender data={data}/>}
      </main>
    </div>
  );
}

function ReporteRender({ data }) {
  const r = data.resumen || {};
  const a = data.alertas || {};
  const p = data.pendientes || {};
  const cm = data.cumplimiento_mes || {};
  const cumplStyle = CUMPL_STYLE[r.cumplimiento_estado] || CUMPL_STYLE.SIN_PLAN;

  return (
    <>
      {/* Encabezado del reporte */}
      <section className="card-base mb-4" data-testid="reporte-header">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500">Reporte ejecutivo</div>
            <h1 className="text-2xl font-bold leading-tight m-0">DMU 614 · {data.fecha}</h1>
            <div className="text-sm text-slate-600">{data.dia_semana} · Generado por {data.generado_por}</div>
          </div>
          <div className="estatus-pill" style={{ background: cumplStyle.bg, color: cumplStyle.color, fontSize:"0.85rem", padding:"0.45rem 0.85rem" }} data-testid="reporte-cumplimiento-badge">
            <i className={`fa-solid ${cumplStyle.icon}`}/>
            <span className="font-bold">Cumplimiento: {r.cumplimiento_pct === null ? "—" : `${r.cumplimiento_pct}%`}</span>
            <span>· {cumplStyle.label}</span>
          </div>
        </div>
        <div className="text-xs text-slate-500 mt-2"><i className="fa-solid fa-info-circle mr-1"/>{r.cumplimiento_nota}</div>
      </section>

      {/* 1. Resumen */}
      <section className="card-base mb-4" data-testid="reporte-resumen">
        <h2 className="text-lg font-bold mb-3">1. Resumen de operación</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Espacios regados" value={r.espacios_regados} color="#16A34A" icon="fa-droplet"/>
          <Stat label="Intervenciones completadas" value={r.intervenciones_completadas} color="#0EA5E9" icon="fa-check"/>
          <Stat label="Pipas activas"  value={`${r.pipas_activas} / ${r.pipas_total}`} color="#0F172A" icon="fa-truck-droplet"/>
          <Stat label="En taller"      value={r.pipas_taller}    color="#DC2626" icon="fa-screwdriver-wrench"/>
          <Stat label="Prestadas"      value={r.pipas_prestadas} color="#D97706" icon="fa-people-arrows"/>
          <Stat label="Parciales"      value={r.intervenciones_parciales} color="#92400E" icon="fa-circle-half-stroke"/>
          <Stat label="En curso"       value={r.intervenciones_en_curso}  color="#475569" icon="fa-spinner"/>
          <Stat label="Litros entregados (total)" value={(((r.litros_totales && r.litros_totales.total) || 0)).toLocaleString("es-MX")} color="#0EA5E9" icon="fa-tint"/>
        </div>

        {/* Desglose por tipo de agua — 2 tarjetas compactas */}
        <div className="grid grid-cols-2 gap-3 mt-3" data-testid="desglose-litros">
          <SubStat
            label="Agua tratada"
            value={(((r.litros_totales && r.litros_totales.tratada) || 0)).toLocaleString("es-MX") + " L"}
            color="#16A34A"
            icon="fa-leaf"
            testid="kpi-litros-tratada"/>
          <SubStat
            label="Agua potable"
            value={(((r.litros_totales && r.litros_totales.potable) || 0)).toLocaleString("es-MX") + " L"}
            color="#0EA5E9"
            icon="fa-droplet"
            testid="kpi-litros-potable"/>
        </div>

        {(r.coberturas_del_dia || []).length > 0 && (
          <div className="mt-4">
            <div className="text-sm font-semibold mb-2"><i className="fa-solid fa-people-arrows mr-1 text-violet-600"/>Coberturas activas hoy: {r.coberturas_del_dia.length}</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tabla-coberturas">
                <thead className="bg-violet-50 text-xs uppercase text-violet-900">
                  <tr><th className="p-2 text-left">Pipa</th><th className="p-2 text-left">Cubre a</th><th className="p-2 text-left">Chofer cobertura</th><th className="p-2 text-left">Motivo</th><th className="p-2 text-left">Zona</th></tr>
                </thead>
                <tbody>
                  {r.coberturas_del_dia.map((c, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-2 mono">{c.pipa || "—"}</td>
                      <td className="p-2">{c.cubre_a || "—"}</td>
                      <td className="p-2">{c.chofer_cobertura || "—"}</td>
                      <td className="p-2"><span className="origen-pill" style={{background:"#EDE9FE",color:"#6D28D9"}}>{c.motivo}</span></td>
                      <td className="p-2">{c.zona || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* 2. Alertas */}
      <section className="card-base mb-4" data-testid="reporte-alertas">
        <h2 className="text-lg font-bold mb-3">2. Alertas</h2>

        {a.suspension_del_dia ? (
          <div className="p-3 rounded-lg mb-3" style={{background:"#FEE2E2", borderLeft:"4px solid #DC2626"}} data-testid="alerta-suspension">
            <div className="font-bold text-red-800"><i className="fa-solid fa-cloud-bolt mr-1"/>SUSPENSIÓN DEL DÍA · {a.suspension_del_dia.folio}</div>
            <div className="text-sm text-red-900 mt-1">
              Motivo: <strong>{a.suspension_del_dia.motivo}</strong> · Alcance: {a.suspension_del_dia.alcance}
              {(a.suspension_del_dia.zonas_afectadas?.length > 0) && <> · Zonas: {a.suspension_del_dia.zonas_afectadas.join(", ")}</>}
            </div>
            {a.suspension_del_dia.comentario && <div className="text-xs text-red-700 italic mt-1">{a.suspension_del_dia.comentario}</div>}
          </div>
        ) : (
          <div className="text-sm text-emerald-700 mb-3"><i className="fa-solid fa-circle-check mr-1"/>Sin suspensiones el día de hoy</div>
        )}

        <ListaItems
          titulo="Pipas en taller"
          icon="fa-screwdriver-wrench"
          color="#DC2626"
          items={a.pipas_en_taller}
          renderItem={(p) => <span><strong className="mono">#{p.id_pipa}</strong> · {p.categoria} · {p.zona}</span>}
          testid="lista-pipas-taller"
        />

        <ListaItems
          titulo="Pipas prestadas a otra zona"
          icon="fa-people-arrows"
          color="#D97706"
          items={a.pipas_prestadas}
          renderItem={(p) => <span><strong className="mono">#{p.id_pipa}</strong> · {p.categoria} · {p.zona_origen} → {p.zona_destino}</span>}
          testid="lista-pipas-prestadas"
        />

        <div className="mt-4" data-testid="lista-sin-regar">
          <div className="text-sm font-semibold mb-2 text-amber-800">
            <i className="fa-solid fa-triangle-exclamation mr-1"/>Espacios sin regar &gt;14 días: <strong>{a.espacios_sin_regar_14dias?.total ?? 0}</strong>
          </div>
          {(a.espacios_sin_regar_14dias?.items || []).length === 0 ? (
            <div className="text-xs text-slate-500">Sin pendientes en este umbral.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-amber-50 text-xs uppercase text-amber-900">
                  <tr><th className="p-2 text-left">Espacio</th><th className="p-2 text-left">Sub-punto</th><th className="p-2 text-left">Última atención</th><th className="p-2 text-right">Días</th></tr>
                </thead>
                <tbody>
                  {a.espacios_sin_regar_14dias.items.slice(0, 12).map((d, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-2">{d.espacio || "—"}</td>
                      <td className="p-2">{d.subpunto || "—"}</td>
                      <td className="p-2 mono text-xs">{d.ultima || "—"}</td>
                      <td className="p-2 text-right font-semibold">{d.dias_sin ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* 3. Pendientes */}
      <section className="card-base mb-4" data-testid="reporte-pendientes">
        <h2 className="text-lg font-bold mb-3">3. Pendientes operativos</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Stat label="Solicitudes de pipa abiertas" value={p.solicitudes_pipa_pendientes} color="#0284C7" icon="fa-hand-holding-droplet"/>
          <Stat label="Solicitudes ciudadanas pendientes" value={p.solicitudes_ciudadanas_pendientes} color="#1E40AF" icon="fa-comment-dots"/>
          <Stat label="Reportes áreas abiertos" value={p.reportes_areas_abiertos} color="#15803D" icon="fa-clipboard-list"/>
        </div>
      </section>

      {/* 4. Cumplimiento mes */}
      <section className="card-base mb-4" data-testid="reporte-cumpl-mes">
        <h2 className="text-lg font-bold mb-3">4. Cumplimiento del mes</h2>
        <div className="flex items-center gap-4 flex-wrap">
          <Stat label={`Mes ${cm.mes}`} value={cm.dias_suspendidos} color="#0891B2" icon="fa-cloud-bolt" sublabel="días suspendidos"/>
          <div className="text-xs text-slate-500 italic max-w-md">
            <i className="fa-solid fa-info-circle mr-1"/>
            {cm.nota}
          </div>
        </div>
      </section>

      <div className="text-xs text-slate-400 text-center py-4" data-testid="reporte-footer">
        Generado automáticamente · DMU 614 · {new Date(data.generado_en).toLocaleString("es-MX")}
      </div>
    </>
  );
}

function Stat({ label, value, color, icon, sublabel }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}>
      <i className={`fa-solid ${icon} text-sm`} style={{color}}/>
      <div className="text-2xl font-bold mt-1" style={{color}}>{value ?? 0}</div>
      <div className="text-xs text-slate-500">{label}</div>
      {sublabel && <div className="text-[10px] text-slate-400">{sublabel}</div>}
    </div>
  );
}

function SubStat({ label, value, color, icon, testid }) {
  return (
    <div
      className="rounded-lg border border-slate-200 flex items-center gap-3"
      data-testid={testid}
      style={{ borderLeft: `4px solid ${color}`, padding: "0.75rem 1rem", background: "#FAFBFC" }}
    >
      <div
        className="grid place-items-center rounded-lg"
        style={{ width: 36, height: 36, background: `${color}22`, color }}
      >
        <i className={`fa-solid ${icon}`} />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-lg font-bold leading-tight" style={{ color }}>{value}</div>
      </div>
    </div>
  );
}

function ListaItems({ titulo, icon, color, items, renderItem, testid }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-3" data-testid={testid}>
      <div className="text-sm font-semibold mb-1" style={{color}}>
        <i className={`fa-solid ${icon} mr-1`}/>{titulo}: {items.length}
      </div>
      <ul className="text-sm space-y-1">
        {items.map((it, i) => <li key={i} className="text-slate-700">• {renderItem(it)}</li>)}
      </ul>
    </div>
  );
}
